import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Drops the border and shadow — for cards nested inside another surface. */
  flush?: boolean;
}

/**
 * A raised plane. Kept under its old name because twenty screens say `Card`,
 * but it is the same object as `Plane`: it separates from the page by light,
 * never by a drawn border, and it never sits inside another one.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { flush = false, className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={clsx(
        'rounded-plane bg-surface',
        !flush && 'shadow-card',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
});

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Right-aligned controls: a filter, an export menu, a link out. */
  actions?: ReactNode;
  /** Retained for callers that set it; headers no longer draw a rule either way. */
  bare?: boolean;
}

export function CardHeader({
  actions,
  bare = false,
  className,
  children,
  ...rest
}: CardHeaderProps) {
  return (
    <div
      className={clsx(
        'flex flex-wrap items-start justify-between gap-3 px-4 pt-4 pb-3 sm:px-5 sm:pt-5',
        className,
      )}
      {...rest}
    >
      <div className="min-w-0 flex-1">{children}</div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  as?: 'h2' | 'h3' | 'h4';
  description?: ReactNode;
}

export function CardTitle({
  as: Tag = 'h3',
  description,
  className,
  children,
  ...rest
}: CardTitleProps) {
  return (
    <div className="min-w-0">
      <Tag
        className={clsx('truncate font-display text-title font-semibold text-ink', className)}
        {...rest}
      >
        {children}
      </Tag>
      {description ? <p className="mt-1 text-micro text-muted">{description}</p> : null}
    </div>
  );
}

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  /** Tables and lists supply their own padding. */
  padded?: boolean;
}

export function CardBody({ padded = true, className, children, ...rest }: CardBodyProps) {
  return (
    <div className={clsx(padded && 'px-4 pt-1 pb-4 sm:px-5 sm:pb-5', className)} {...rest}>
      {children}
    </div>
  );
}

export function CardFooter({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx(
        'flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3 sm:px-5',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
